/* ==========================================================================
   Crumpaw · BlossomPup Engraved Collar — buy box controller.

   <cp-bp-buybox> drives the cp-bp-main section; the document-level handlers
   at the bottom connect the size table (cp-bp-sizes) and the "engrave their
   collar" buttons further down the page to it through events, so each
   section works on its own and none of them reaches into another.

   Degradation contract: if anything here throws, the form still sells a
   collar. Print and size are real radios, there is a <noscript> variant
   select, and the name and phone inputs carry their own properties[...].

   Money is never invented: every price is the real variant's cents run
   through the shop's money format.

   The plate follows one rule: once a name is typed it shows exactly the
   sanitised values the form will submit; before that it shows the labelled
   example. Fields are cleaned in place (allow-list, then the cap) so input,
   plate and line item property can never disagree.

   The form always posts ONE collar. The optional lead is its own product and
   follows as a separate line after the form's add lands; if it fails, the
   collar comes back out so the cart never disagrees with the total shown.
   ========================================================================== */

(() => {
  'use strict';

  if (typeof window === 'undefined' || !window.customElements) return;
  if (customElements.get('cp-bp-buybox')) return;

  const CART_UPDATE = 'cart:update';
  const SOURCE = 'cp-bp';
  const PENDING_TTL = 20000;
  const NAME_ALLOWED = /[^A-Za-z0-9 '\-À-ÖØ-öø-ſ]/g;
  const PHONE_ALLOWED = /[^0-9 +()\-]/g;

  const qs = (root, name) => root.querySelector(`[data-cp-bp="${name}"]`);
  const qsa = (root, name) => Array.from(root.querySelectorAll(`[data-cp-bp="${name}"]`));
  const setText = (node, value) => {
    if (node) node.textContent = value == null ? '' : String(value);
  };
  const show = (node, on) => {
    if (node) node.hidden = !on;
  };

  const formatMoney = (cents, format) => {
    const amount = Number(cents) || 0;
    const group = (value, precision, thousands, decimal) => {
      const parts = (value / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
    };
    if (typeof format !== 'string' || format.indexOf('{{') === -1) {
      return `$${group(amount, 2, ',', '.')}`;
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_m, token) => {
      switch (token) {
        case 'amount_no_decimals':
          return group(amount, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(amount, 2, "'", '.');
        default:
          return group(amount, 2, ',', '.');
      }
    });
  };

  const interpolate = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
    );

  /** Allow-list, no leading or doubled spaces, upper-case for names, then the cap. */
  const clean = (value, allowed, max, upper) => {
    let next = String(value || '')
      .replace(allowed, '')
      .replace(/^\s+/, '')
      .replace(/\s{2,}/g, ' ');
    if (upper) next = next.toUpperCase();
    return Array.from(next).slice(0, max || 12).join('');
  };

  const sanitiseField = (input, allowed, max, upper) => {
    if (!input) return;
    const raw = String(input.value || '');
    const next = clean(raw, allowed, max, upper);
    if (next === raw) return;
    const caret = input.selectionStart;
    const removed = raw.length - next.length;
    input.value = next;
    if (caret !== null && input.setSelectionRange) {
      const at = Math.max(0, Math.min(next.length, caret - removed));
      try {
        input.setSelectionRange(at, at);
      } catch (error) {
        /* some input types refuse setSelectionRange */
      }
    }
  };

  const readCart = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const changeLine = (key, quantity) =>
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    }).then((r) => r.json());

  const addToCart = (items) =>
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((response) =>
        response.json().then((data) => {
          if (!response.ok) throw new Error((data && data.description) || 'Cart request failed');
          return data;
        })
      )
      .then((data) => {
        document.dispatchEvent(
          new CustomEvent(CART_UPDATE, { bubbles: true, detail: { data: { source: SOURCE } } })
        );
        return data;
      });

  /* =========================================================================
     Page-level wiring. Registered before the element is defined, because
     defining it upgrades the buy box synchronously and its first render
     announces the selected size straight away.
     ========================================================================= */

  /* The size table picks a size in the buy box. */
  document.addEventListener('click', (event) => {
    const pick = event.target.closest && event.target.closest('[data-cp-bp-size-pick]');
    if (pick) {
      event.preventDefault();
      document.dispatchEvent(
        new CustomEvent('cpbp:select', { detail: { size: pick.getAttribute('data-cp-bp-size-pick') } })
      );
      return;
    }
    const scroll = event.target.closest && event.target.closest('[data-cp-bp-scroll-buy]');
    if (scroll) {
      const target = document.querySelector('cp-bp-buybox [data-cp-bp="buy"]');
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  /* ...and shows which size the buy box has selected. */
  document.addEventListener('cpbp:size', (event) => {
    const size = String((event.detail && event.detail.size) || '').toLowerCase();
    document.querySelectorAll('[data-cp-bp-size-row]').forEach((row) => {
      const on = String(row.getAttribute('data-cp-bp-size-row')).toLowerCase() === size;
      row.dataset.selected = String(on);
      const button = row.querySelector('[data-cp-bp-size-pick]');
      if (button) {
        setText(button, on ? button.dataset.labelSelected : button.dataset.labelChoose);
        button.setAttribute('aria-pressed', String(on));
      }
    });
  });

  class CpBpBuybox extends HTMLElement {
    connectedCallback() {
      if (this.dataset.cpBpReady === 'true') return;
      const configEl = qs(this, 'config');
      if (!configEl) return;
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (error) {
        return;
      }
      if (!this.config || !Array.isArray(this.config.variants)) return;

      this.dataset.cpBpReady = 'true';
      this.copy = this.config.copy || {};
      this.keys = this.config.keys || {};
      this.moneyFormat = this.config.moneyFormat || '${{amount}}';

      this.refs = {
        variantId: qs(this, 'variant-id'),
        cta: qs(this, 'cta'),
        ctaHint: qs(this, 'cta-hint'),
        error: qs(this, 'error'),
        track: qs(this, 'track'),
        nameInput: qs(this, 'name'),
        nameCount: qs(this, 'name-count'),
        phoneInput: qs(this, 'phone'),
        phoneCount: qs(this, 'phone-count'),
        nameOnly: qs(this, 'name-only'),
        propColour: qs(this, 'prop-colour'),
        propSize: qs(this, 'prop-size'),
        lightbox: qs(this, 'lightbox'),
        lightboxImg: qs(this, 'lightbox-img'),
        lightboxHint: qs(this, 'lightbox-hint'),
        rail: qs(this, 'rail'),
        railPrev: qs(this, 'rail-prev'),
        railNext: qs(this, 'rail-next'),
        sizeModal: qs(this, 'size-modal'),
        leadCard: qs(this, 'lead-card'),
        leadToggle: qs(this, 'lead-toggle'),
        leadTitle: qs(this, 'lead-title'),
        leadSub: qs(this, 'lead-sub'),
        leadPicker: qs(this, 'lead-picker'),
        leadModal: qs(this, 'lead-modal'),
        leadMatch: qs(this, 'lead-match'),
      };

      this.lists = {
        priceNow: qsa(this, 'price-now'),
        priceWas: qsa(this, 'price-was'),
        priceSave: qsa(this, 'price-save'),
        total: qsa(this, 'total'),
        totalWas: qsa(this, 'total-was'),
        totalSave: qsa(this, 'total-save'),
        summary: qsa(this, 'summary'),
        sizeRows: qsa(this, 'size-row'),
        sizePicks: qsa(this, 'size-pick'),
        ctaLabel: qsa(this, 'cta-label'),
        colourLabel: qsa(this, 'colour-label'),
        colourImg: qsa(this, 'colour-img'),
        plate: qsa(this, 'plate'),
        plateName: qsa(this, 'plate-name'),
        platePhone: qsa(this, 'plate-phone'),
        plateNote: qsa(this, 'plate-note'),
        thumbs: qsa(this, 'thumb'),
        slides: qsa(this, 'slide'),
        leadImg: qsa(this, 'lead-img'),
        leadLabel: qsa(this, 'lead-label'),
      };

      this.colourInputs = qsa(this, 'colour');
      this.sizeInputs = qsa(this, 'size');
      this.leadInputs = qsa(this, 'lead-colour');

      const start = this.lists.slides.find((el) => el.hasAttribute('data-start'));
      this.state = {
        media: (start || this.lists.slides[0] || { dataset: {} }).dataset.key || 'colour',
        leadOn: false,
        leadPicked: false,
        sizeModal: false,
        leadModal: false,
      };
      this.hasColourSlide = this.lists.slides.some((el) => el.dataset.key === 'colour');
      this.pending = null;
      this.lastSize = null;

      this.wire();
      this.wireStage();
      this.wireRail();
      this.wireLightbox();
      this.wireVideos();
      this.render();
    }

    disconnectedCallback() {
      if (this.onCartUpdate) document.removeEventListener(CART_UPDATE, this.onCartUpdate);
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      if (this.onSelect) document.removeEventListener('cpbp:select', this.onSelect);
      if (this.videoObserver) this.videoObserver.disconnect();
      if (this.stageObserver) this.stageObserver.disconnect();
      if (this.railObserver) this.railObserver.disconnect();
      if (this.syncRail) window.removeEventListener('resize', this.syncRail);
      document.body.classList.remove('cp-bp-locked');
    }

    /* ------------------------------------------------------------ selection */

    checked(inputs) {
      return inputs.find((input) => input.checked) || null;
    }

    colour() {
      const input = this.checked(this.colourInputs);
      return input ? input.value : (this.config.variants[0] || {}).colour || '';
    }

    size() {
      const input = this.checked(this.sizeInputs);
      return input ? input.value : (this.config.variants[0] || {}).size || '';
    }

    colourMeta() {
      const value = String(this.colour()).toLowerCase();
      const colours = this.config.colours || [];
      return colours.find((c) => String(c.value).toLowerCase() === value) || colours[0] || {};
    }

    variant() {
      const colour = String(this.colour()).toLowerCase();
      const size = String(this.size()).toLowerCase();
      return (
        this.config.variants.find(
          (v) => String(v.colour).toLowerCase() === colour && String(v.size).toLowerCase() === size
        ) || null
      );
    }

    lead() {
      const options = this.config.leads || [];
      const input = this.checked(this.leadInputs);
      return (input && options.find((o) => String(o.id) === input.value)) || options[0] || null;
    }

    /** The lead print the collar suggests, until the shopper picks one. */
    syncLeadDefault() {
      if (this.state.leadPicked || !this.leadInputs.length) return;
      const want = String(this.colourMeta().lead || '').toLowerCase();
      if (!want) return;
      const match = (this.config.leads || []).find(
        (o) => o.available !== false && String(o.value).toLowerCase() === want
      );
      if (!match) return;
      const input = this.leadInputs.find((i) => i.value === String(match.id));
      if (input && !input.checked && !input.disabled) input.checked = true;
    }

    /* --------------------------------------------------------------- fields */

    nameOnly() {
      return !!(this.refs.nameOnly && this.refs.nameOnly.checked);
    }

    name() {
      if (!this.refs.nameInput) return '';
      return clean(this.refs.nameInput.value, NAME_ALLOWED, this.config.nameMax, true).trim();
    }

    phone() {
      if (!this.refs.phoneInput || this.nameOnly()) return '';
      return clean(this.refs.phoneInput.value, PHONE_ALLOWED, this.config.phoneMax, false).trim();
    }

    /* ----------------------------------------------------------------- wire */

    wire() {
      this.addEventListener('input', (event) => {
        const target = event.target;
        if (target === this.refs.nameInput) {
          sanitiseField(target, NAME_ALLOWED, this.config.nameMax, true);
        } else if (target === this.refs.phoneInput) {
          sanitiseField(target, PHONE_ALLOWED, this.config.phoneMax, false);
        } else {
          return;
        }
        this.render();
      });

      this.addEventListener('change', (event) => {
        const target = event.target;
        if (!target) return;
        if (
          (target === this.refs.nameInput || target === this.refs.phoneInput) &&
          target.value !== target.value.trim()
        ) {
          target.value = target.value.trim();
        }
        if (this.colourInputs.includes(target) && this.hasColourSlide) this.state.media = 'colour';
        if (this.leadInputs.includes(target)) this.state.leadPicked = true;
        if (target === this.refs.leadToggle) {
          this.state.leadOn = target.checked;
          if (!this.state.leadOn) this.state.leadModal = false;
        }
        this.render();
      });

      this.addEventListener('click', (event) => {
        const el = event.target;
        const thumb = el.closest('[data-cp-bp="thumb"]');
        if (thumb) {
          event.preventDefault();
          this.state.media = thumb.dataset.key || 'colour';
          this.render();
          return;
        }
        /* A row in the size guide picks that size and closes the dialog, so
           the guide finishes the job it started instead of leaving the shopper
           to remember the answer and go and find the size chips again. */
        const pick = el.closest('[data-cp-bp="size-pick"]');
        if (pick) {
          event.preventDefault();
          const want = String(pick.dataset.size || '').toLowerCase();
          const input = this.sizeInputs.find((i) => String(i.value).toLowerCase() === want);
          if (input && !input.disabled) input.checked = true;
          this.closeModals();
          return;
        }
        if (el.closest('[data-cp-bp="size-open"]')) {
          event.preventDefault();
          this.openModal('sizeModal', el.closest('button'));
          return;
        }
        if (el.closest('[data-cp-bp="lead-open"]')) {
          event.preventDefault();
          this.openModal('leadModal', el.closest('button'));
          return;
        }
        if (
          el.closest('[data-cp-bp="modal-close"]') ||
          el === this.refs.sizeModal ||
          el === this.refs.leadModal
        ) {
          event.preventDefault();
          this.closeModals();
        }
      });

      this.onKeydown = (event) => {
        if (event.key !== 'Escape') return;
        /* The lightbox sits above the other two, so it is dismissed first. */
        if (this.refs.lightbox && !this.refs.lightbox.hidden) {
          if (this.closeLightbox) this.closeLightbox();
          return;
        }
        if (this.state.sizeModal || this.state.leadModal) this.closeModals();
      };
      document.addEventListener('keydown', this.onKeydown);

      /* Blocked, not disabled, while the name is empty — a disabled button
         fires no click to hang "here is the field you missed" on. Capture
         phase so Horizon's submit never sees a blocked add. */
      if (this.refs.cta) {
        this.refs.cta.addEventListener(
          'click',
          (event) => {
            if (this.refs.cta.getAttribute('aria-disabled') !== 'true') {
              setText(this.refs.error, '');
              this.pending = { items: this.followLines(), at: Date.now(), name: this.name() };
              return;
            }
            event.preventDefault();
            event.stopImmediatePropagation();
            if (this.variant() && this.variant().available) this.nudgeName();
          },
          true
        );
      }

      this.onCartUpdate = (event) => {
        const source = event.detail && event.detail.data && event.detail.data.source;
        if (source === SOURCE) return;
        const pending = this.pending;
        this.pending = null;
        if (!pending || !pending.items.length) return;
        if (Date.now() - pending.at > PENDING_TTL) return;
        this.addRest(pending);
      };
      document.addEventListener(CART_UPDATE, this.onCartUpdate);

      /* A size picked in the size table lower down the page. */
      this.onSelect = (event) => {
        const size = String((event.detail && event.detail.size) || '').toLowerCase();
        const input = this.sizeInputs.find((i) => String(i.value).toLowerCase() === size);
        if (!input || input.disabled) return;
        input.checked = true;
        this.render();
        const buy = qs(this, 'buy');
        if (buy) buy.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };
      document.addEventListener('cpbp:select', this.onSelect);
    }

    openModal(which, opener) {
      this.opener = opener || null;
      this.state[which] = true;
      this.render();
      const modal = which === 'sizeModal' ? this.refs.sizeModal : this.refs.leadModal;
      const close = modal && qs(modal, 'modal-close');
      if (close) close.focus();
    }

    closeModals() {
      this.state.sizeModal = false;
      this.state.leadModal = false;
      this.render();
      if (this.opener && this.opener.focus) this.opener.focus();
    }

    nudgeName() {
      const input = this.refs.nameInput;
      if (!input) return;
      input.setAttribute('aria-invalid', 'true');
      input.focus({ preventScroll: true });
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /* --------------------------------------------------------------- render */

    render() {
      const variant = this.variant();
      const colour = this.colourMeta();
      const name = this.name();
      const nameOnly = this.nameOnly();
      const phone = this.phone();

      /* A combination that does not exist cannot be chosen; one that exists
         but is sold out stays selectable and says so, rather than leaving the
         shopper unable to click it and unsure why. */
      this.sizeInputs.forEach((input) => {
        const match = this.config.variants.find(
          (v) =>
            String(v.colour).toLowerCase() === String(this.colour()).toLowerCase() &&
            String(v.size).toLowerCase() === String(input.value).toLowerCase()
        );
        input.disabled = !match;
        input.dataset.soldout = String(!!match && !match.available);
      });

      if (this.refs.variantId && variant) this.refs.variantId.value = variant.id;

      /* ---- money */
      const unit = variant ? Number(variant.price) || 0 : 0;
      const compare = variant ? Number(variant.compareAt) || 0 : 0;
      this.syncLeadDefault();
      const lead = this.lead();
      const leadPrice = lead ? Number(lead.price) || 0 : 0;
      const total = unit + (this.state.leadOn && lead ? leadPrice : 0);

      this.lists.priceNow.forEach((node) => setText(node, formatMoney(unit, this.moneyFormat)));
      this.lists.priceWas.forEach((node) => {
        show(node, compare > unit);
        setText(node, formatMoney(compare, this.moneyFormat));
      });
      this.lists.priceSave.forEach((node) => {
        const off = compare > unit ? compare - unit : 0;
        show(node, off > 0);
        setText(
          node,
          interpolate(this.copy.save || 'SAVE {amount}', {
            amount: formatMoney(off, this.moneyFormat),
            percent: `${compare > 0 ? Math.round((off / compare) * 100) : 0}%`,
          })
        );
      });
      this.lists.total.forEach((node) => setText(node, formatMoney(total, this.moneyFormat)));

      /* The total carries the same evidence as the headline price: the lead is
         added to both sides, so the saving shown is the collar's saving and
         not a discount on an add-on that never had one. */
      const compareTotal = compare > unit ? compare + (this.state.leadOn && lead ? leadPrice : 0) : 0;
      const totalOff = compareTotal > total ? compareTotal - total : 0;
      this.lists.totalWas.forEach((node) => {
        show(node, totalOff > 0);
        setText(node, formatMoney(compareTotal, this.moneyFormat));
      });
      this.lists.totalSave.forEach((node) => {
        show(node, totalOff > 0);
        setText(
          node,
          interpolate(this.copy.save || 'SAVE {amount}', {
            amount: formatMoney(totalOff, this.moneyFormat),
            percent: `${compareTotal > 0 ? Math.round((totalOff / compareTotal) * 100) : 0}%`,
          })
        );
      });

      /* ---- print, gallery, summary */
      this.lists.colourImg.forEach((img) => {
        if (colour.image && img.getAttribute('src') !== colour.image) img.src = colour.image;
      });
      this.lists.colourLabel.forEach((node) => setText(node, colour.label || this.colour()));
      const summary = interpolate(
        this.state.leadOn && lead ? this.copy.summaryLead : this.copy.summary,
        { colour: colour.short || colour.label || this.colour(), size: this.size() }
      );
      this.lists.summary.forEach((node) => setText(node, summary));
      this.showSlide(this.state.media);
      this.lists.thumbs.forEach((button) => {
        button.setAttribute('aria-current', String(button.dataset.key === this.state.media));
      });

      /* ---- the plate */
      const example = !name;
      const plateName = example ? this.copy.plateName || '' : name;
      const platePhone = example ? (nameOnly ? '' : this.copy.platePhone || '') : phone;
      this.lists.plate.forEach((node) => {
        node.dataset.example = String(example);
      });
      this.lists.plateName.forEach((node) => setText(node, plateName));
      this.lists.platePhone.forEach((node) => {
        setText(node, platePhone);
        node.hidden = !platePhone;
      });
      this.lists.plateNote.forEach((node) => show(node, example));

      if (this.refs.phoneInput) this.refs.phoneInput.disabled = nameOnly;
      if (this.refs.nameInput && name) this.refs.nameInput.removeAttribute('aria-invalid');
      setText(this.refs.nameCount, `${name.length}/${this.config.nameMax || 10}`);
      setText(
        this.refs.phoneCount,
        nameOnly ? this.copy.phoneNotEngraved || '' : `${phone.length}/${this.config.phoneMax || 12}`
      );

      if (this.refs.propColour) this.refs.propColour.value = colour.label || this.colour();
      if (this.refs.propSize) this.refs.propSize.value = this.size();

      /* ---- the lead */
      if (this.refs.leadToggle) this.refs.leadToggle.checked = this.state.leadOn;
      if (this.refs.leadCard) this.refs.leadCard.dataset.on = String(this.state.leadOn);
      show(this.refs.leadPicker, this.state.leadOn);
      if (lead) {
        const price = formatMoney(leadPrice, this.moneyFormat);
        setText(
          this.refs.leadTitle,
          interpolate(this.state.leadOn ? this.copy.leadTitleOn : this.copy.leadTitleOff, { price })
        );
        setText(
          this.refs.leadSub,
          interpolate(this.state.leadOn ? this.copy.leadSubOn : this.copy.leadSubOff, { lead: lead.value })
        );
        this.lists.leadImg.forEach((img) => {
          if (lead.image && img.getAttribute('src') !== lead.image) img.src = lead.image;
          img.alt = interpolate(this.copy.leadAlt || '{lead}', { lead: lead.value });
        });
        this.lists.leadLabel.forEach((node) => setText(node, lead.value));
        setText(
          this.refs.leadMatch,
          interpolate(this.copy.leadCollar || '{colour}', { colour: colour.label || this.colour() })
        );
      }

      /* ---- the size guide's own rows */
      const currentSize = String(this.size()).toLowerCase();
      this.lists.sizeRows.forEach((row) => {
        row.dataset.current = String(String(row.dataset.size || '').toLowerCase() === currentSize);
      });
      this.lists.sizePicks.forEach((button) => {
        const on = String(button.dataset.size || '').toLowerCase() === currentSize;
        button.dataset.current = String(on);
        button.setAttribute('aria-pressed', String(on));
        const label = button.querySelector('[data-cp-bp="size-pick-label"]');
        if (!label) return;
        if (!label.dataset.choose) label.dataset.choose = label.textContent;
        setText(label, on ? this.copy.sizePicked || label.dataset.choose : label.dataset.choose);
      });

      /* ---- dialogs */
      if (this.refs.sizeModal) this.refs.sizeModal.hidden = !this.state.sizeModal;
      if (this.refs.leadModal) this.refs.leadModal.hidden = !this.state.leadModal;
      document.body.classList.toggle('cp-bp-locked', this.state.sizeModal || this.state.leadModal);

      /* ---- the button */
      const sellable = !!(variant && variant.available);
      const ready = sellable && !!name;
      if (this.refs.cta) {
        this.refs.cta.setAttribute('aria-disabled', String(!ready));
        this.refs.cta.dataset.state = !sellable ? 'soldout' : ready ? 'ready' : 'blocked';
      }
      const label = !sellable
        ? this.copy.ctaSoldOut || ''
        : !name
          ? this.copy.ctaBlocked || ''
          : interpolate(this.copy.ctaReady || '{amount}', { amount: formatMoney(total, this.moneyFormat) });
      this.lists.ctaLabel.forEach((node) => setText(node, label));
      show(this.refs.ctaHint, sellable && !name);

      /* ---- tell the size table */
      if (this.size() !== this.lastSize) {
        this.lastSize = this.size();
        document.dispatchEvent(new CustomEvent('cpbp:size', { detail: { size: this.lastSize } }));
      }

      this.painted = true;
    }

    /** Scroll the stage to a slide and pause any video that is not on screen. */
    showSlide(key) {
      const track = this.refs.track;
      if (!track) return;
      this.lists.slides.forEach((el) => {
        const video = el.querySelector('video');
        if (video && el.dataset.key !== key && !video.paused) video.pause();
      });
      const slide = this.lists.slides.find((el) => el.dataset.key === key);
      if (!slide) return;
      if (Math.abs(track.scrollLeft - slide.offsetLeft) < 2) return;
      this.autoScrolling = true;
      window.clearTimeout(this.autoScrollTimer);
      this.autoScrollTimer = window.setTimeout(() => {
        this.autoScrolling = false;
      }, 600);
      track.scrollTo({ left: slide.offsetLeft, behavior: this.painted ? 'smooth' : 'auto' });
    }

    /**
     * The gallery lightbox: click the stage photo to see it big, click the big
     * one to magnify.
     *
     * The magnified image scales about the point that was clicked, which means
     * the origin has to be worked out from the image's UNSCALED box. Once
     * `transform: scale()` is on, getBoundingClientRect() reports the scaled
     * box, so the rect is captured at the moment of zooming and reused for
     * every later pointer move — reading it live walks the origin away from
     * the pointer.
     *
     * A video slide has no <img>, so it is left alone: tapping a video should
     * play it, not open a still of it.
     */
    wireLightbox() {
      const box = this.refs.lightbox;
      const img = this.refs.lightboxImg;
      if (!box || !img) return;

      let baseRect = null;

      const setHint = (zoomed) => {
        if (!this.refs.lightboxHint) return;
        const text = zoomed ? this.copy.zoomHintOut : this.copy.zoomHint;
        setText(this.refs.lightboxHint, text || '');
        this.refs.lightboxHint.hidden = !text;
      };

      const unzoom = () => {
        baseRect = null;
        box.dataset.zoom = 'false';
        img.style.transformOrigin = '50% 50%';
        setHint(false);
      };

      const originFrom = (event) => {
        const rect = baseRect || img.getBoundingClientRect();
        const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
        const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
        return `${x}% ${y}%`;
      };

      const close = () => {
        box.hidden = true;
        unzoom();
        document.body.classList.remove('cp-bp-locked');
        if (this.lightboxOpener && this.lightboxOpener.focus) {
          this.lightboxOpener.focus({ preventScroll: true });
        }
        this.lightboxOpener = null;
      };
      this.closeLightbox = close;

      this.addEventListener('click', (event) => {
        const slide = event.target.closest('[data-cp-bp="slide"]');
        if (!slide || box.contains(event.target)) return;
        const source = slide.tagName === 'IMG' ? slide : slide.querySelector('img');
        if (!source) return;
        event.preventDefault();
        img.src = source.dataset.cpBpFull || source.currentSrc || source.src;
        img.alt = source.alt || '';
        this.lightboxOpener = source;
        box.hidden = false;
        unzoom();
        document.body.classList.add('cp-bp-locked');
        const button = qs(box, 'lightbox-close');
        if (button) button.focus();
      });

      box.addEventListener('click', (event) => {
        if (event.target === img) {
          if (box.dataset.zoom === 'true') {
            unzoom();
            return;
          }
          baseRect = img.getBoundingClientRect();
          img.style.transformOrigin = originFrom(event);
          box.dataset.zoom = 'true';
          setHint(true);
          return;
        }
        /* The backdrop and the close button both dismiss it. */
        close();
      });

      box.addEventListener('pointermove', (event) => {
        if (box.dataset.zoom !== 'true') return;
        img.style.transformOrigin = originFrom(event);
      });
    }

    /**
     * The thumbnail rail's arrows.
     *
     * They page by whatever is actually visible rather than a fixed number of
     * thumbs, so the same code is right for a wide column and a 360px phone.
     * The whole control hides itself when nothing overflows — a product with
     * three photos should not be offered arrows that do nothing — and each
     * arrow hides at its end of the track rather than sitting there greyed
     * out, because a rail that ends is not an error state.
     */
    wireRail() {
      const rail = this.refs.rail;
      if (!rail) return;

      const page = (direction) => {
        const step = Math.max(rail.clientWidth * 0.8, 120);
        rail.scrollBy({ left: step * direction, behavior: 'smooth' });
      };
      if (this.refs.railPrev) this.refs.railPrev.addEventListener('click', () => page(-1));
      if (this.refs.railNext) this.refs.railNext.addEventListener('click', () => page(1));

      this.syncRail = () => {
        /* Sub-pixel layout means scrollLeft rarely lands exactly on 0 or on
           the maximum, so both ends need a tolerance. */
        const slack = 2;
        const overflows = rail.scrollWidth - rail.clientWidth > slack;
        show(this.refs.railPrev, overflows && rail.scrollLeft > slack);
        show(
          this.refs.railNext,
          overflows && rail.scrollLeft + rail.clientWidth < rail.scrollWidth - slack
        );
      };

      rail.addEventListener('scroll', this.syncRail, { passive: true });
      if (typeof ResizeObserver === 'function') {
        this.railObserver = new ResizeObserver(this.syncRail);
        this.railObserver.observe(rail);
      } else {
        window.addEventListener('resize', this.syncRail);
      }
      this.syncRail();
    }

    wireStage() {
      const track = this.refs.track;
      if (!track) return;
      let idle;
      track.addEventListener(
        'scroll',
        () => {
          if (this.autoScrolling) return;
          window.clearTimeout(idle);
          idle = window.setTimeout(() => {
            const middle = track.scrollLeft + track.clientWidth / 2;
            let nearest = null;
            let best = Infinity;
            this.lists.slides.forEach((el) => {
              const distance = Math.abs(el.offsetLeft + el.offsetWidth / 2 - middle);
              if (distance < best) {
                best = distance;
                nearest = el;
              }
            });
            if (nearest && nearest.dataset.key !== this.state.media) {
              this.state.media = nearest.dataset.key;
              this.render();
            }
          }, 90);
        },
        { passive: true }
      );
    }

    /* A gallery video starts when its slide is the one on screen. Bare
       `autoplay` is not enough here: the slide begins off to the side of a
       horizontally scrolling snap track, so the browser sees a video that is
       not visible and declines.

       Reduce Motion is deliberately NOT a blanket block. Bailing out under it
       meant the clip never played at all on a phone with the setting on, and a
       muted, silent product loop is not the motion that setting exists to
       suppress — WCAG 2.2.2 asks for a way to STOP auto-playing motion, and
       these videos carry `controls`. Where the browser or the OS refuses
       anyway (iOS Low Power Mode does, and no site can override it), the catch
       leaves a usable player rather than a dead frame. */
    wireVideos() {
      const videos = Array.from(this.querySelectorAll('.cp-bp-slide video'));
      const track = this.refs.track;
      if (!videos.length || !track || !('IntersectionObserver' in window)) return;

      /* Two conditions, because one observer cannot see both: the track
         observer knows which slide is in the stage, and the stage observer
         knows whether the gallery is still on the page at all. */
      const inStage = new WeakMap();
      let galleryOnPage = true;

      const apply = (video) => {
        if (galleryOnPage && inStage.get(video)) {
          video.muted = true;
          const started = video.play();
          if (started && started.catch) started.catch(() => {
            video.controls = true;
          });
        } else if (!video.paused) {
          video.pause();
        }
      };

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            inStage.set(entry.target, entry.isIntersecting);
            apply(entry.target);
          });
        },
        { root: track, threshold: 0.6 }
      );

      /* Without this the clip played forever: a slide is fully inside the
         track whatever the page is doing, so scrolling the gallery off the
         screen never paused it. That is a battery cost anywhere, and on iOS
         it is a correctness bug — WebKit plays one video at a time, so the
         "how it goes on" clip further down could not start while this one was
         still running out of sight. */
      const stage = this.querySelector('.cp-bp-stage') || track;
      this.stageObserver = new IntersectionObserver(
        (entries) => {
          galleryOnPage = entries.some((entry) => entry.isIntersecting);
          videos.forEach(apply);
        },
        { threshold: 0.2 }
      );
      this.stageObserver.observe(stage);

      videos.forEach((video) => {
        video.dataset.cpBpObserved = '1';
        inStage.set(video, false);
        this.videoObserver.observe(video);
      });
    }

    /* ------------------------------------------------------------- checkout */

    followLines() {
      const lead = this.lead();
      return this.state.leadOn && lead && lead.id ? [{ id: lead.id, quantity: 1 }] : [];
    }

    addRest(pending) {
      const variant = this.variant();
      readCart()
        .then((cart) => {
          const lines = (cart.items || []).filter(
            (item) =>
              variant &&
              String(item.variant_id) === String(variant.id) &&
              item.properties &&
              item.properties[this.keys.name] === pending.name
          );
          const line = lines[lines.length - 1] || null;
          return addToCart(pending.items).catch((error) => {
            if (!line) throw error;
            return changeLine(line.key, Math.max(0, line.quantity - 1)).then(() => {
              throw error;
            });
          });
        })
        .then(() => setText(this.refs.error, ''))
        .catch(() => {
          setText(this.refs.error, this.copy.addFailed || '');
          document.dispatchEvent(
            new CustomEvent(CART_UPDATE, { bubbles: true, detail: { data: { source: SOURCE } } })
          );
        });
    }
  }

  customElements.define('cp-bp-buybox', CpBpBuybox);

  /* =========================================================================
     <cp-bp-rail> — the reviews grid and its full-review dialog.

     Cards clamp to three lines so a row stays level whatever length the
     reviews are, and any card opens in full on click or tap at every width.
     The dialog is handed a CLONE of the card rather than a second copy of the
     block's settings, so the popup and the card cannot drift apart.
     ========================================================================= */
  const RV = (name) => `[data-cp-bp-rv="${name}"]`;

  class CpBpRail extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector(RV('modal'));
      this.body = this.querySelector(RV('modal-body'));
      this.opener = null;

      this.addEventListener('click', (event) => {
        const target = event.target;
        if (!target || !target.closest) return;
        const open = target.closest(RV('open'));
        if (open) {
          event.preventDefault();
          this.open(open.closest(RV('card')), open);
          return;
        }
        if (target.closest(RV('close')) || target === this.modal) {
          event.preventDefault();
          this.close();
        }
      });

      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.modal && !this.modal.hidden) this.close();
      };
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      document.body.classList.remove('cp-bp-locked');
    }

    open(card, opener) {
      if (!card || !this.modal || !this.body) return;
      const copy = card.cloneNode(true);
      copy.removeAttribute('data-cp-bp-rv');
      /* The block id belongs to the card the theme editor already knows
         about; a second element carrying it would give it two targets. */
      copy.removeAttribute('data-shopify-editor-block');
      copy.querySelectorAll('[data-cp-bp-rv], .cp-bp-rcard__more').forEach((node) => node.remove());
      this.body.textContent = '';
      this.body.appendChild(copy);

      this.opener = opener || null;
      this.modal.hidden = false;
      document.body.classList.add('cp-bp-locked');
      const close = this.modal.querySelector(RV('close'));
      if (close) close.focus();
    }

    close() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.body.classList.remove('cp-bp-locked');
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }
  }

  if (!customElements.get('cp-bp-rail')) customElements.define('cp-bp-rail', CpBpRail);

  /* The same treatment for a video that is not in the gallery — the "how it
     goes on" section further down the page. It is far enough down that the
     browser has usually thrown the autoplay attribute away by the time it is
     visible, so it is started on intersection instead. */
  const wireStandaloneVideos = () => {
    if (!('IntersectionObserver' in window)) return;
    const videos = Array.from(document.querySelectorAll('video[data-cp-bp-autoplay]')).filter(
      (video) => !video.closest('cp-bp-buybox') && !video.dataset.cpBpObserved
    );
    if (!videos.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.muted = true;
            const started = video.play();
            if (started && started.catch) started.catch(() => {
              video.controls = true;
            });
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.35 }
    );

    videos.forEach((video) => {
      video.dataset.cpBpObserved = '1';
      observer.observe(video);
    });
  };

  wireStandaloneVideos();
  /* Re-run for a section the theme editor drops in after load. */
  document.addEventListener('shopify:section:load', wireStandaloneVideos);
})();
